import { useMemo } from 'react';
import { v4 as uuidv4 } from 'uuid';
import dayjs from 'dayjs';
import { useTheme } from '@material-ui/core/styles';
import { useTranslation } from 'react-i18next';
import {
  mapMarkerAccountOutlinePath,
  mapMarkerAccountPath,
  mapMarkerPath,
  mapMarkerStarPath,
} from '../components/markers';
import { fill, stroke } from 'pdf-lib';
import { zIndex } from 'styled-system';

const useMarkers = ({
  records,
  projects,
  selectedPosition,
  selectPositionMode,
}) => {
  const theme = useTheme();
  const { t } = useTranslation('common');
  // 🟢 Selected marker
  const selectedMarker = useMemo(() => {
    if (!selectedPosition) return [];

    return [
      {
        id: 'selected-position',
        position: {
          lat: Number(selectedPosition.latitude),
          lng: Number(selectedPosition.longitude),
        },
        icon: {
          path: mapMarkerPath,
          scale: 2,
          color: theme.palette.primary.main,
          fillOpacity: 0.6,
          strokeOpacity: 0.8,
        },
        draggable: !!selectPositionMode,
        options: {
          cursor: selectPositionMode ? 'grab' : 'not-allowed',
          dragCursor: selectPositionMode ? 'grabbing' : 'default',
          zIndex: 3,
        },
      },
    ];
  }, [
    selectedPosition,
    selectPositionMode,
    mapMarkerPath,
    theme.palette.primary.main,
  ]);

  // 🟣 Record markers
  const recordMarkers = useMemo(() => {
    if (!records) return [];

    return records.flatMap((record) => {
      const markers = [];

      if (record.latIn) {
        markers.push({
          id: uuidv4(),
          desc: `${t('location.logged_in')} ${dayjs(record.clockInAt).format(
            'MMMM D YYYY, hh:mm:ss'
          )}`,
          position: {
            lat: Number(record.latIn),
            lng: Number(record.longIn),
          },
          icon: {
            path: mapMarkerAccountPath,
            scale: 2,
            color: theme.palette.success.main,
          },
          options: {
            zIndex: 3,
          },
        });
      }

      if (record.latOut) {
        markers.push({
          id: uuidv4(),
          desc: `${t('location.logged_out')} ${dayjs(record.clockOutAt).format(
            'MMMM D YYYY, hh:mm:ss'
          )}`,
          position: {
            lat: Number(record.latOut),
            lng: Number(record.longOut),
          },
          icon: {
            path: mapMarkerAccountOutlinePath,
            scale: 2,
            color: theme.palette.error.main,
          },
          options: {
            zIndex: 3,
          },
        });
      }

      return markers;
    });
  }, [
    records,
    t,
    mapMarkerAccountPath,
    mapMarkerAccountOutlinePath,
    theme.palette,
  ]);

  // 🔵 Project markers
  const projectMarkers = useMemo(() => {
    if (!projects) return [];

    return projects
      .filter((p) => p.latitude)
      .map((p) => ({
        id: p.id,
        desc: `${p.projectKey} - ${p.name}`,
        address: `${p.street1 || ''} ${p.street2 || ''} ${p.postcode || ''} ${
          p.city || ''
        }`.trim(),
        position: {
          lat: Number(p.latitude),
          lng: Number(p.longitude),
        },
        icon: {
          path:
            p.businessTypeEnum === 'EMPLOYER'
              ? mapMarkerStarPath
              : mapMarkerPath,
          scale: 2,
          color:
            p.businessTypeEnum === 'EMPLOYER'
              ? p.mainColor || theme.palette.primary.main
              : theme.palette.primary.main,
        },
        options: {
          zIndex: p.businessTypeEnum === 'EMPLOYER' ? 4 : 3,
        },
      }));
  }, [projects, mapMarkerPath, theme.palette.primary.main]);

  // ⚪ Combine all marker types
  const allMarkers = useMemo(() => {
    const all = [...recordMarkers, ...projectMarkers, ...selectedMarker];
    return all;
  }, [recordMarkers, projectMarkers, selectedMarker]);

  return { recordMarkers, projectMarkers, selectedMarker, allMarkers };
};

export default useMarkers;
